'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FileText, Calendar, List, Loader2, ArrowRight, ArrowLeft, Image as ImageIcon, RefreshCw, Pencil } from 'lucide-react';
import type { Event, EventType } from '@/models/event';
import { useFirestore } from '@/firebase';
import { parseFirebaseIndexError } from '@/lib/firebase-index-error';
import { FirebaseIndexErrorDialog } from '@/components/features/firebase-index-error-dialog';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PaperSize } from '@/lib/pdf/pdf-constants';

// Exporters
import { exportListFormat } from '@/lib/pdf/exporters/list-exporter';
import { exportAnnualFormat } from '@/lib/pdf/exporters/annual-exporter';
import { exportMonthlyCalendarFormat } from '@/lib/pdf/exporters/monthly-exporter';
import { exportSaddleStitchFormat } from '@/lib/pdf/exporters/saddle-stitch-exporter';
import { exportMonthlyListFormat } from '@/lib/pdf/exporters/monthly-list-exporter';

// Utils
import { fetchUniqueRandomImages } from '@/lib/pdf/pdf-utils';
import { getPacificMonthKey } from '@/lib/date-utils';
import { GalleryImagePicker } from './gallery-image-picker';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import Image from 'next/image';
import { useCalendarPresets } from '@/hooks/use-calendar-presets';
import { CalendarPresetManager } from './calendar-preset-manager';
import type { CalendarPreset } from '@/lib/calendar-presets-db';

interface EventPDFExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  events: Event[];
  eventTypes: EventType[];
  getEventTypeName: (typeId: string) => string;
}

type PDFFormat = 'list' | 'annual' | 'monthly-calendar' | 'monthly-list' | 'saddle-stitch';
type Step = 'format' | 'images';

export function EventPDFExportDialog({
  open,
  onOpenChange,
  events,
  eventTypes,
  getEventTypeName,
}: EventPDFExportDialogProps) {
  const [selectedFormat, setSelectedFormat] = useState<PDFFormat>('list');
  const [paperSize, setPaperSize] = useState<PaperSize>('letter');

  // Year Selection
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // State
  const [step, setStep] = useState<Step>('format');
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // Image Selection State
  const [relevantMonthKeys, setRelevantMonthKeys] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<Record<string, string>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeMonthKey, setActiveMonthKey] = useState<string | null>(null);

  // Errors
  const [indexErrorDialogOpen, setIndexErrorDialogOpen] = useState(false);
  const [indexErrorUrl, setIndexErrorUrl] = useState<string | undefined>();
  const [indexErrorMessage, setIndexErrorMessage] = useState<string | undefined>();

  // Presets
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const { presets, save: savePreset, remove: removePreset } = useCalendarPresets();

  const firestore = useFirestore();

  // Reset state on open/close
  useEffect(() => {
    if (open) {
      // Calculate available years
      const years = new Set<number>();
      const currentYear = new Date().getFullYear();
      years.add(currentYear);
      years.add(currentYear + 1); // Always offer next year too

      events.forEach(e => {
        const y = parseInt(getPacificMonthKey(e.date).split('-')[0]);
        if (!isNaN(y)) years.add(y);
      });

      const sortedYears = Array.from(years).sort((a, b) => b - a);
      setAvailableYears(sortedYears);

      // Default to current year if available, else first one
      if (sortedYears.includes(currentYear)) {
        setSelectedYear(currentYear);
      } else {
        setSelectedYear(sortedYears[0]);
      }

    } else {
      setStep('format');
      setSelectedImages({});
      setRelevantMonthKeys([]);
      setIsLoadingImages(false);
      setActivePresetId(null);
    }
  }, [open, events]);

  const handleLoadPreset = useCallback((preset: CalendarPreset) => {
    setSelectedFormat(preset.selectedFormat);
    setPaperSize(preset.paperSize);
    setSelectedYear(preset.selectedYear);
    setSelectedImages(preset.selectedImages);
    setActivePresetId(preset.id);
  }, []);

  const currentPresetState = useMemo(() => ({
    selectedFormat,
    paperSize,
    selectedYear,
    selectedImages,
  }), [selectedFormat, paperSize, selectedYear, selectedImages]);

  const needsImageSelection = selectedFormat === 'saddle-stitch' || selectedFormat === 'monthly-calendar';
  const needsYearSelection = needsImageSelection || selectedFormat === 'monthly-list' || selectedFormat === 'annual';

  const handleNext = async () => {
    if (needsImageSelection && step === 'format') {
      setIsLoadingImages(true);
      try {
        // Calculate Months for Selected Year
        const keys = new Set<string>();
        const yearStr = selectedYear.toString();

        for (let m = 1; m <= 12; m++) {
          keys.add(`${yearStr}-${String(m).padStart(2, '0')}`);
        }

        const sortedKeys = Array.from(keys).sort();
        setRelevantMonthKeys(sortedKeys);

        // Fetch Initial Random Images
        // Only fetch for keys we don't have yet
        const missingKeys = sortedKeys.filter(k => !selectedImages[k]);

        if (missingKeys.length > 0) {
          const randoms = await fetchUniqueRandomImages(firestore, missingKeys);
          setSelectedImages(prev => ({ ...prev, ...randoms }));
        }

        setStep('images');
      } catch (e) {
        console.error("Failed to prepare images", e);
      } finally {
        setIsLoadingImages(false);
      }
    } else {
      await doExport();
    }
  };

  const doExport = async () => {
    setIsExporting(true);
    // Small delay to allow UI to update with loading state
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      if (selectedFormat === 'list') {
        await exportListFormat(events, eventTypes, getEventTypeName, firestore, paperSize);
      } else if (selectedFormat === 'annual') {
        await exportAnnualFormat(events, eventTypes, firestore, paperSize, selectedYear);
      } else if (selectedFormat === 'monthly-calendar') {
        await exportMonthlyCalendarFormat(events, firestore, paperSize, selectedImages, selectedYear);
      } else if (selectedFormat === 'monthly-list') {
        await exportMonthlyListFormat(events, eventTypes, getEventTypeName, firestore, paperSize, selectedYear);
      } else if (selectedFormat === 'saddle-stitch') {
        await exportSaddleStitchFormat(events, firestore, paperSize, selectedImages, selectedYear);
      }

      await new Promise(resolve => setTimeout(resolve, 100));
      onOpenChange(false);

    } catch (error) {
      console.error('Error exporting PDF:', error);

      // Check if it's a Firebase index error
      const indexError = parseFirebaseIndexError(error);
      if (indexError.isIndexError) {
        setIndexErrorUrl(indexError.indexUrl);
        setIndexErrorMessage(indexError.errorMessage);
        setIndexErrorDialogOpen(true);
      } else {
        alert('Error generating PDF. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleImageSelect = (url: string) => {
    if (activeMonthKey) {
      setSelectedImages(prev => ({ ...prev, [activeMonthKey]: url }));
    }
    setPickerOpen(false);
    setActiveMonthKey(null);
  };

  const openPicker = (key: string) => {
    setActiveMonthKey(key);
    setPickerOpen(true);
  };

  const randomizeImage = async (key: string) => {
    try {
      // Fetch one random (unique not guaranteed in single fetch but sufficiently random)
      const result = await fetchUniqueRandomImages(firestore, [key]);
      if (result[key]) {
        setSelectedImages(prev => ({ ...prev, [key]: result[key] }));
      }
    } catch (e) {
      console.error("Failed to randomize", e);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={step === 'images' ? "sm:max-w-[900px] h-[80vh] flex flex-col" : "sm:max-w-[500px]"}>
          <DialogHeader>
            <DialogTitle>
              {step === 'format' ? 'Export Options' : `Select Images for ${selectedYear}`}
            </DialogTitle>
            <DialogDescription>
              {step === 'format'
                ? 'Choose format and options for the events PDF.'
                : 'Review and customize the images for each month.'}
            </DialogDescription>
          </DialogHeader>

          <CalendarPresetManager
            presets={presets}
            activePresetId={activePresetId}
            currentState={currentPresetState}
            onLoad={handleLoadPreset}
            onSave={async (preset) => {
              const saved = await savePreset(preset);
              setActivePresetId(saved.id);
              return saved;
            }}
            onDelete={async (id) => {
              await removePreset(id);
              if (activePresetId === id) setActivePresetId(null);
            }}
          />

          {step === 'format' && (
            <div className="grid gap-4 py-4">
              <div className="space-y-4">
                <Label>Format</Label>
                <RadioGroup
                  value={selectedFormat}
                  onValueChange={(v) => setSelectedFormat(v as PDFFormat)}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="flex items-center space-x-2 border p-3 rounded-md has-[button[data-state=checked]]:border-primary">
                    <RadioGroupItem value="list" id="fmt-list" />
                    <Label htmlFor="fmt-list" className="flex items-center gap-2 cursor-pointer">
                      <List className="w-4 h-4" /> List View
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border p-3 rounded-md has-[button[data-state=checked]]:border-primary">
                    <RadioGroupItem value="annual" id="fmt-annual" />
                    <Label htmlFor="fmt-annual" className="flex items-center gap-2 cursor-pointer">
                      <Calendar className="w-4 h-4" /> Annual Grid
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border p-3 rounded-md has-[button[data-state=checked]]:border-primary">
                    <RadioGroupItem value="monthly-calendar" id="fmt-mcal" />
                    <Label htmlFor="fmt-mcal" className="flex items-center gap-2 cursor-pointer">
                      <Calendar className="w-4 h-4" /> Monthly (Wall)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border p-3 rounded-md has-[button[data-state=checked]]:border-primary">
                    <RadioGroupItem value="monthly-list" id="fmt-mlist" />
                    <Label htmlFor="fmt-mlist" className="flex items-center gap-2 cursor-pointer">
                      <List className="w-4 h-4" /> Monthly List
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border p-3 rounded-md has-[button[data-state=checked]]:border-primary col-span-2">
                    <RadioGroupItem value="saddle-stitch" id="fmt-saddle" />
                    <Label htmlFor="fmt-saddle" className="flex items-center gap-2 cursor-pointer">
                      <FileText className="w-4 h-4" /> Wall Folding Calendar
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {needsYearSelection && (
                <div className="space-y-4">
                  <Label>Calendar Year</Label>
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(v) => setSelectedYear(parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map(year => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-4">
                <Label>Paper Size</Label>
                <Select value={paperSize} onValueChange={(v) => setPaperSize(v as PaperSize)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select paper size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="letter">Letter (8.5" x 11")</SelectItem>
                    <SelectItem value="legal">Legal (8.5" x 14")</SelectItem>
                    <SelectItem value="tabloid">Tabloid (11" x 17")</SelectItem>
                    <SelectItem value="a4">A4 (210mm x 297mm)</SelectItem>
                    <SelectItem value="a3">A3 (297mm x 420mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 'images' && (
            <ScrollArea className="flex-1 pr-4 -mr-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
                {relevantMonthKeys.map(key => (
                  <Card key={key} className="overflow-hidden group relative">
                    <div className="aspect-[4/3] bg-muted relative">
                      {selectedImages[key] ? (
                        <Image
                          src={selectedImages[key]}
                          alt={key}
                          fill
                          className="object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <ImageIcon className="w-8 h-8 opacity-50" />
                        </div>
                      )}

                      {/* Overlay Actions */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button size="icon" variant="secondary" onClick={() => openPicker(key)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="secondary" onClick={() => randomizeImage(key)}>
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="p-2 text-center text-sm font-semibold bg-background border-t">
                      {key}
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="flex justify-between sm:justify-between mt-4">
            {step === 'images' ? (
              <Button variant="outline" onClick={() => setStep('format')} disabled={isExporting}>
                <ArrowLeft className="mr-2 w-4 h-4" /> Back
              </Button>
            ) : (
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
                Cancel
              </Button>
            )}

            <Button onClick={handleNext} disabled={isExporting || isLoadingImages}>
              {isExporting || isLoadingImages ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {step === 'format' && needsImageSelection ? (
                <>Next <ArrowRight className="ml-2 w-4 h-4" /></>
              ) : (
                'Export PDF'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GalleryImagePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handleImageSelect}
        currentValue={activeMonthKey ? selectedImages[activeMonthKey] : undefined}
        title="Select Image"
        description="Browse albums, upload, or enter a URL to select an image for this month."
      />

      <FirebaseIndexErrorDialog
        open={indexErrorDialogOpen}
        onOpenChange={setIndexErrorDialogOpen}
        indexUrl={indexErrorUrl}
        errorMessage={indexErrorMessage}
      />
    </>
  );
}
